import type { ChangeEvent, FC, KeyboardEvent } from 'react';

import type {
  CampusBannerInput,
  CampusFormValues,
  CampusListItem,
  CampusStatus,
} from '@/types/campus.types';

const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  // Union Territories
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
] as const;

interface CampusFormModalProps {
  campus: CampusListItem | null;
  formValues: CampusFormValues;
  domainInput: string;
  deptInput: string;
  formError: string | null;
  isSaving: boolean;
  isUploadingBanner: boolean;
  bannerUploadError: string | null;
  /** Applying this banner to every campus — only offered while editing an existing one. */
  isApplyingBannerToAll: boolean;
  applyAllResult: string | null;
  onApplyBannerToAll: () => void;
  onChangeField: (field: keyof CampusFormValues, value: string | string[] | CampusStatus) => void;
  onChangeDomainInput: (value: string) => void;
  onAddDomain: () => void;
  onRemoveDomain: (domain: string) => void;
  onChangeDeptInput: (value: string) => void;
  onAddDept: () => void;
  onRemoveDept: (index: number) => void;
  onChangeBannerField: (field: keyof CampusBannerInput, value: string | boolean) => void;
  onUploadBannerImage: (file: File) => void;
  onRemoveBannerImage: () => void;
  onSubmit: () => void;
  onClose: () => void;
}

const inputBase =
  'w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 py-2 text-[13px] text-[var(--color-ink)] outline-none transition-colors placeholder:text-[var(--color-ink-muted)] focus:border-brand-400 focus:ring-2 focus:ring-brand-100';

const CampusFormModal: FC<CampusFormModalProps> = ({
  campus,
  formValues,
  domainInput,
  deptInput,
  formError,
  isSaving,
  isUploadingBanner,
  bannerUploadError,
  isApplyingBannerToAll,
  applyAllResult,
  onApplyBannerToAll,
  onChangeField,
  onChangeDomainInput,
  onAddDomain,
  onRemoveDomain,
  onChangeDeptInput,
  onAddDept,
  onRemoveDept,
  onChangeBannerField,
  onUploadBannerImage,
  onRemoveBannerImage,
  onSubmit,
  onClose,
}) => {
  const isEdit = campus !== null;
  const banner = formValues.banner;

  const handleBannerFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) onUploadBannerImage(file);
    e.target.value = '';
  };

  const handleDomainKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      onAddDomain();
    }
  };

  const handleDeptKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      onAddDept();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-xl">
        <div className="max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--color-divider)] px-6 py-4">
            <h2 className="text-[15px] font-bold text-[var(--color-ink)]">
              {isEdit ? 'Edit Campus' : 'Add Campus'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-full text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-4 px-6 py-5">
            {/* Name */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
                Campus Name <span className="text-[var(--color-danger-ink)]">*</span>
              </label>
              <input
                type="text"
                className={inputBase}
                placeholder="e.g. IIT Bombay"
                value={formValues.name}
                onChange={(e) => onChangeField('name', e.target.value)}
              />
            </div>

            {/* City + State */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">City</label>
                <input
                  type="text"
                  className={inputBase}
                  placeholder="Mumbai"
                  value={formValues.city}
                  onChange={(e) => onChangeField('city', e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">State</label>
                <select
                  className={inputBase}
                  value={formValues.state}
                  onChange={(e) => onChangeField('state', e.target.value)}
                >
                  <option value="">Select state…</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">Status</label>
              <select
                className={inputBase}
                value={formValues.status}
                onChange={(e) => onChangeField('status', e.target.value as CampusStatus)}
              >
                <option value="active">Active — open for registrations</option>
                <option value="paused">Paused — no new registrations</option>
              </select>
            </div>

            {/* Email Domains */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
                Email Domains
              </label>
              <p className="text-[11px] text-[var(--color-ink-muted)]">
                Allowed domains for student sign-up (e.g. iitb.ac.in). Press Enter or comma to add.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  className={inputBase}
                  placeholder="iitb.ac.in"
                  value={domainInput}
                  onChange={(e) => onChangeDomainInput(e.target.value)}
                  onKeyDown={handleDomainKeyDown}
                />
                <button
                  type="button"
                  onClick={onAddDomain}
                  className="shrink-0 rounded-lg border border-brand-400 bg-brand-50 px-3 py-2 text-[12px] font-semibold text-brand-600 transition-colors hover:bg-brand-100"
                >
                  Add
                </button>
              </div>
              {formValues.emailDomains.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {formValues.emailDomains.map((domain) => (
                    <span
                      key={domain}
                      className="flex items-center gap-1 rounded-md bg-[var(--color-surface-muted)] px-2 py-1 text-[11px] font-medium text-[var(--color-ink-muted)]"
                    >
                      @{domain}
                      <button
                        type="button"
                        onClick={() => onRemoveDomain(domain)}
                        className="ml-0.5 text-[10px] text-[var(--color-ink-muted)] hover:text-[var(--color-danger-ink)]"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Departments */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
                Departments
              </label>
              <p className="text-[11px] text-[var(--color-ink-muted)]">
                Students pick one at sign-up (e.g. Computer Science). Press Enter or comma to add.
                Leave empty to let students type their own.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  className={inputBase}
                  placeholder="Computer Science"
                  value={deptInput}
                  onChange={(e) => onChangeDeptInput(e.target.value)}
                  onKeyDown={handleDeptKeyDown}
                />
                <button
                  type="button"
                  onClick={onAddDept}
                  className="shrink-0 rounded-lg border border-brand-400 bg-brand-50 px-3 py-2 text-[12px] font-semibold text-brand-600 transition-colors hover:bg-brand-100"
                >
                  Add
                </button>
              </div>
              {formValues.departments.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {formValues.departments.map((dept, index) => (
                    <span
                      key={dept.id ?? `new-${index}`}
                      className="flex items-center gap-1 rounded-md bg-[var(--color-surface-muted)] px-2 py-1 text-[11px] font-medium text-[var(--color-ink-muted)]"
                    >
                      {dept.name}
                      <button
                        type="button"
                        onClick={() => onRemoveDept(index)}
                        className="ml-0.5 text-[10px] text-[var(--color-ink-muted)] hover:text-[var(--color-danger-ink)]"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Home Banner */}
            <div className="flex flex-col gap-3 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-muted)] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[13px] font-bold text-[var(--color-ink)]">Home Banner</p>
                  <p className="text-[11px] text-[var(--color-ink-muted)]">
                    Shown at the top of the home screen for students in this campus.
                  </p>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-[12px] font-semibold text-[var(--color-ink-muted)]">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-500"
                    checked={banner.enabled}
                    onChange={(e) => onChangeBannerField('enabled', e.target.checked)}
                  />
                  {banner.enabled ? 'Visible' : 'Hidden'}
                </label>
              </div>

              {banner.enabled ? (
                <>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
                      Label
                    </label>
                    <input
                      type="text"
                      maxLength={40}
                      className={inputBase}
                      placeholder="FROM SHOUTT TEAM"
                      value={banner.label}
                      onChange={(e) => onChangeBannerField('label', e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
                      Message
                    </label>
                    <input
                      type="text"
                      maxLength={120}
                      className={inputBase}
                      placeholder="Tech Fest: 50% off rentals this weekend!"
                      value={banner.message}
                      onChange={(e) => onChangeBannerField('message', e.target.value)}
                    />
                    <p className="text-[11px] text-[var(--color-ink-muted)]">
                      Leave empty to keep the banner hidden until you have something to announce.
                    </p>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
                      Image <span className="font-normal">(optional)</span>
                    </label>
                    {banner.imageUrl ? (
                      <div className="relative overflow-hidden rounded-lg border border-[var(--color-divider)]">
                        <img
                          src={banner.imageUrl}
                          alt="Banner preview"
                          className="h-28 w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={onRemoveBannerImage}
                          className="absolute right-2 top-2 rounded-md bg-black/60 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-black/80"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-[var(--color-divider)] bg-white px-3 py-6 text-[12px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:border-brand-400 hover:text-brand-600">
                        {isUploadingBanner ? 'Uploading…' : 'Click to upload an image'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={isUploadingBanner}
                          onChange={handleBannerFile}
                        />
                      </label>
                    )}
                    {bannerUploadError ? (
                      <p className="text-[11px] font-medium text-[var(--color-danger-ink)]">
                        {bannerUploadError}
                      </p>
                    ) : null}
                  </div>

                  {/* Tap → Contact screen */}
                  <label className="flex cursor-pointer items-start gap-2 text-[12px] font-semibold text-[var(--color-ink-muted)]">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 accent-brand-500"
                      checked={banner.tapOpensContact}
                      onChange={(e) => onChangeBannerField('tapOpensContact', e.target.checked)}
                    />
                    <span>
                      Tapping the banner opens the Contact screen
                      <span className="mt-0.5 block font-normal text-[var(--color-ink-muted)]">
                        Students can reach Shoutt and send a message.
                      </span>
                    </span>
                  </label>

                  {banner.tapOpensContact ? (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
                        Contact screen text <span className="font-normal">(optional)</span>
                      </label>
                      <textarea
                        rows={3}
                        maxLength={500}
                        className={`${inputBase} resize-none`}
                        placeholder="A short message shown at the top of the Contact screen for this campus…"
                        value={banner.contactText}
                        onChange={(e) => onChangeBannerField('contactText', e.target.value)}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>

            {formError ? (
              <div className="rounded-lg border border-[var(--color-danger-ink)]/30 bg-[var(--color-danger-bg)] px-3 py-2 text-[12px] font-medium text-[var(--color-danger-ink)]">
                {formError}
              </div>
            ) : null}
          </div>

          {/* Footer. The apply-to-all sits on the left, away from Save: it writes this banner
              onto every campus, and it should not be reachable by aiming at the primary button.
              Offered only when editing — a campus that does not exist yet has no banner to
              broadcast. */}
          <div className="flex items-center justify-between gap-3 border-t border-[var(--color-divider)] px-6 py-4">
            <div className="flex min-w-0 items-center gap-3">
              {isEdit ? (
                <button
                  type="button"
                  onClick={onApplyBannerToAll}
                  disabled={isSaving || isUploadingBanner || isApplyingBannerToAll}
                  className="cursor-pointer rounded-lg border border-[var(--color-divider)] bg-white px-3.5 py-2 text-[12.5px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:border-brand-300 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isApplyingBannerToAll ? 'Applying…' : '📣 Apply this banner to all campuses'}
                </button>
              ) : null}
              {applyAllResult ? (
                <span className="truncate text-[12px] font-medium text-emerald-700">
                  {applyAllResult}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-4 py-2 text-[13px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={isSaving || isUploadingBanner || formValues.name.trim().length < 2}
              className="rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Campus'}
            </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CampusFormModal;
